import React, { useEffect } from 'react';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  children: React.ReactNode;
  footer?: React.ReactNode;
  closeOnOverlayClick?: boolean;
}

export interface ModalHeaderProps {
  children: React.ReactNode;
  onClose?: () => void;
}

export interface ModalBodyProps {
  children: React.ReactNode;
  className?: string;
}

export interface ModalFooterProps {
  children: React.ReactNode;
  className?: string;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  size = 'md',
  children,
  footer,
  closeOnOverlayClick = true
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Desktop size classes (only apply on md and up)
  const sizeClasses = {
    sm: 'md:max-w-md',
    md: 'md:max-w-lg',
    lg: 'md:max-w-2xl',
    xl: 'md:max-w-4xl'
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center pb-16 md:pb-0 md:p-4 bg-black bg-opacity-50 backdrop-blur-sm"
      onClick={handleOverlayClick}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
        className={`bg-vb-surface border-t md:border border-vb-border rounded-t-xl md:rounded-lg shadow-vb-lg w-full ${sizeClasses[size]} max-h-[calc(100vh-4rem)] md:max-h-[90vh] flex flex-col dark:bg-vb-surface-dark dark:border-vb-border-dark dark:shadow-vb-lg-dark animate-slide-up md:animate-fade-in`}
      >
        {/* Use composition pattern if children contain sub-components, otherwise use title/footer props */}
        {title ? (
          <>
            {/* Header */}
            <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 border-b border-vb-border dark:border-vb-border-dark">
              <h2
                id="modal-title"
                className="text-lg md:text-xl font-semibold text-vb-text-primary dark:text-vb-text-primary-dark"
              >
                {title}
              </h2>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="text-vb-text-secondary hover:text-vb-text-primary transition-colors dark:text-vb-text-secondary-dark dark:hover:text-vb-text-primary-dark p-1"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4">{children}</div>

            {/* Footer */}
            {footer && (
              <div className="flex-shrink-0 flex items-center justify-end gap-3 px-4 md:px-6 pt-3 md:pt-4 pb-4 md:pb-4 border-t border-vb-border dark:border-vb-border-dark">
                {footer}
              </div>
            )}
          </>
        ) : (
          // Composition pattern - children contain ModalHeader, ModalBody, ModalFooter
          <>{children}</>
        )}
      </div>
    </div>
  );
};

// Sub-components for flexible modal composition
export const ModalHeader: React.FC<ModalHeaderProps> = ({ children, onClose }) => (
  <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 border-b border-vb-border dark:border-vb-border-dark">
    <h2
      id="modal-title"
      className="text-lg md:text-xl font-semibold text-vb-text-primary dark:text-vb-text-primary-dark"
    >
      {children}
    </h2>
    {onClose && (
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="text-vb-text-secondary hover:text-vb-text-primary transition-colors dark:text-vb-text-secondary-dark dark:hover:text-vb-text-primary-dark p-1"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    )}
  </div>
);

export const ModalBody: React.FC<ModalBodyProps> = ({ children, className = '' }) => (
  <div className={`flex-1 overflow-y-auto px-4 md:px-6 py-4 ${className}`}>{children}</div>
);

export const ModalFooter: React.FC<ModalFooterProps> = ({ children, className = '' }) => (
  <div
    className={`flex-shrink-0 flex items-center justify-end gap-3 px-4 md:px-6 pt-3 md:pt-4 pb-4 md:pb-4 border-t border-vb-border dark:border-vb-border-dark ${className}`}
  >
    {children}
  </div>
);
